<template>
  <div class="w-full">
    <!-- Filter Controls Header -->
    <div class="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
      <!-- Filter Header -->
      <div class="p-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900 flex items-center">
            <i class="fas fa-filter text-indigo-600 mr-2" />
            Filter Organizations
          </h3>
          <button
            class="text-sm text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
            @click="toggleFilterExpansion">
            <i
              :class="filtersExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
              class="mr-1" />
            {{ filtersExpanded ? 'Collapse' : 'Expand' }} Filters
          </button>
        </div>
      </div>

      <!-- Search and Basic Controls - Always Visible -->
      <div class="p-4">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Search Input -->
          <div class="flex-1 min-w-[300px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              <i class="fas fa-search text-gray-400 mr-1" />
              Search Customers
            </label>
            <input
              v-model="search"
              type="text"
              class="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors duration-200"
              placeholder="Search by name or email...">
          </div>

          <!-- Hide Deleted Checkbox -->
          <div class="flex items-center self-end pb-1">
            <label class="flex items-center cursor-pointer hover:text-gray-900 transition-colors duration-200">
              <input
                v-model="showDeleted"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-2
                       focus:ring-indigo-500 transition-colors duration-200">
              <span class="ml-2 text-sm text-gray-700">Hide deleted organizations</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Collapsible Filters Section -->
      <div
        v-show="filtersExpanded"
        class="border-t border-gray-200 transition-all duration-300 bg-gray-50">
        <!-- Filter Categories Tabs -->
        <div class="border-b border-gray-200 bg-white">
          <div class="px-4">
            <nav class="-mb-px flex space-x-4 overflow-x-auto">
              <button
                class="py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 whitespace-nowrap"
                :class="activeFilterCategory === 'business'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                @click="activeFilterCategory = 'business'">
                <i class="fas fa-building mr-2" />
                Business
                <span
                  v-if="getBusinessFiltersCount() > 0"
                  class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {{ getBusinessFiltersCount() }}
                </span>
              </button>
              <button
                class="py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 whitespace-nowrap"
                :class="activeFilterCategory === 'billing'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                @click="activeFilterCategory = 'billing'">
                <i class="fas fa-credit-card mr-2" />
                Billing & Plans
                <span
                  v-if="getBillingFiltersCount() > 0"
                  class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {{ getBillingFiltersCount() }}
                </span>
              </button>
              <button
                class="py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 whitespace-nowrap"
                :class="activeFilterCategory === 'features'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                @click="activeFilterCategory = 'features'">
                <i class="fas fa-puzzle-piece mr-2" />
                Features & Services
                <span
                  v-if="getFeaturesFiltersCount() > 0"
                  class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {{ getFeaturesFiltersCount() }}
                </span>
              </button>
              <button
                class="py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 whitespace-nowrap"
                :class="activeFilterCategory === 'technical'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                @click="activeFilterCategory = 'technical'">
                <i class="fas fa-cogs mr-2" />
                Technical
                <span
                  v-if="getTechnicalFiltersCount() > 0"
                  class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {{ getTechnicalFiltersCount() }}
                </span>
              </button>
            </nav>
          </div>
        </div>

        <!-- Business & Organization Filters -->
        <div
          v-show="activeFilterCategory === 'business'"
          class="p-4 bg-white border-b border-gray-200">
          <div class="mb-3">
            <p class="text-xs text-gray-500">
              Filter by customer ownership, onboarding state, sentiment, orders, and location count
            </p>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <!-- Customer Owner Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-building text-gray-400 mr-1" />
                Customer Owner
              </label>
              <select
                v-model="customerOwnerFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Owners
                </option>
                <option value="linda">
                  Linda
                </option>
                <option value="pin_parrot">
                  Pin Parrot
                </option>
                <option value="constant_social">
                  Constant Social
                </option>
              </select>
            </div>

            <!-- Onboarding State Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-user-check text-gray-400 mr-1" />
                Onboarding State
              </label>
              <select
                v-model="onboardingStateFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All States
                </option>
                <option value="pending_contact">
                  ⏳ Pending Contact
                </option>
                <option value="contacted">
                  📞 Contacted
                </option>
                <option value="onboarded">
                  ✓ Onboarded
                </option>
              </select>
            </div>

            <!-- Sentiment Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-smile text-gray-400 mr-1" />
                Sentiment
              </label>
              <select
                v-model="sentimentFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Sentiments
                </option>
                <option value="pending">
                  ⏳ Pending
                </option>
                <option value="happy">
                  😊 Happy
                </option>
                <option value="ok">
                  😐 OK
                </option>
                <option value="upset">
                  😟 Upset
                </option>
              </select>
            </div>

            <!-- Order Submitted Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-shopping-cart text-gray-400 mr-1" />
                Order Status
              </label>
              <select
                v-model="orderSubmittedFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Orders
                </option>
                <option value="true">
                  ✓ Submitted
                </option>
                <option value="false">
                  ❌ Not Submitted
                </option>
              </select>
            </div>

            <!-- Location Count Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-map-marker-alt text-gray-400 mr-1" />
                Location Count
              </label>
              <select
                v-model="locationCountFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="single_location">
                  Single Location
                </option>
                <option value="multiple_locations">
                  Multiple Locations
                </option>
              </select>
            </div>

            <!-- Monthly Reports Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-file-alt text-gray-400 mr-1" />
                Monthly Reports
              </label>
              <select
                v-model="monthlyReportsFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="needs_report">
                  📋 Needs Report (Overdue)
                </option>
                <option value="needs_report_soon">
                  ⏰ Needs Report in 7 Days
                </option>
                <option value="no_report_needed">
                  ✓ No Report Needed
                </option>
              </select>
            </div>

            <!-- Triage Tickets Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-ticket-alt text-gray-400 mr-1" />
                Triage Tickets
              </label>
              <select
                v-model="triageTicketsFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="with_active_tickets">
                  🔴 With Active Tickets
                </option>
                <option value="with_any_tickets">
                  📋 Ever Had Tickets
                </option>
                <option value="without_tickets">
                  ✓ No Tickets
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Billing & Plans Filters -->
        <div
          v-show="activeFilterCategory === 'billing'"
          class="p-4 bg-white border-b border-gray-200">
          <div class="mb-3">
            <p class="text-xs text-gray-500">
              Filter by subscription plans, payment status, and discounts
            </p>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <!-- Plan Group Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-layer-group text-gray-400 mr-1" />
                Plan Group
              </label>
              <select
                v-model="subscriptionPlanFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Plan Groups
                </option>
                <option
                  v-for="group in planGroups"
                  :key="group.value"
                  :value="group.value">
                  {{ group.label }}
                </option>
                <option value="not_subscribed">
                  Not Subscribed
                </option>
              </select>
            </div>

            <!-- Individual Plan Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-credit-card text-gray-400 mr-1" />
                Specific Plan
              </label>
              <select
                v-model="subscriptionPlanIdFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Plans
                </option>
                <option
                  v-for="plan in plans"
                  :key="plan.value"
                  :value="plan.value">
                  {{ plan.label }}
                </option>
              </select>
            </div>

            <!-- Payment Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-exclamation-circle text-gray-400 mr-1" />
                Payment Status
              </label>
              <select
                v-model="paymentStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="good_standing">
                  ✓ Good Standing
                </option>
                <option value="delinquent">
                  ⚠️ Delinquent
                </option>
                <option value="payment_issues">
                  ⚠️ Payment Issues
                </option>
                <option value="outstanding_balance">
                  ⚠️ Outstanding Balance
                </option>
                <option value="failed_payments">
                  ❌ Failed Payments
                </option>
              </select>
            </div>

            <!-- Discount Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-tag text-gray-400 mr-1" />
                Discount Status
              </label>
              <select
                v-model="discountStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="with_discounts">
                  With Discounts
                </option>
                <option value="without_discounts">
                  Without Discounts
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Features & Services Filters -->
        <div
          v-show="activeFilterCategory === 'features'"
          class="p-4 bg-white border-b border-gray-200">
          <div class="mb-3">
            <p class="text-xs text-gray-500">
              Filter by enabled features and service statuses
            </p>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <!-- Feature Location Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-map text-gray-400 mr-1" />
                Location Feature
              </label>
              <select
                v-model="featureLocationFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="true">
                  ✓ Enabled
                </option>
                <option value="false">
                  ❌ Disabled
                </option>
              </select>
            </div>

            <!-- Feature Chat Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-comments text-gray-400 mr-1" />
                Chat Feature
              </label>
              <select
                v-model="featureChatFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="true">
                  ✓ Enabled
                </option>
                <option value="false">
                  ❌ Disabled
                </option>
              </select>
            </div>

            <!-- Feature Leads Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-user-plus text-gray-400 mr-1" />
                Leads Feature
              </label>
              <select
                v-model="featureLeadsFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="true">
                  ✓ Enabled
                </option>
                <option value="false">
                  ❌ Disabled
                </option>
              </select>
            </div>

            <!-- Feature Reputation Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-star text-gray-400 mr-1" />
                Reputation Feature
              </label>
              <select
                v-model="featureReputationFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="true">
                  ✓ Enabled
                </option>
                <option value="false">
                  ❌ Disabled
                </option>
              </select>
            </div>

            <!-- Geogrid Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-map-marked-alt text-gray-400 mr-1" />
                Geogrid Status
              </label>
              <select
                v-model="geogridStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="with_geogrids">
                  With Geogrids
                </option>
                <option value="without_geogrids">
                  Without Geogrids
                </option>
              </select>
            </div>

            <!-- Rep Campaign Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-bullhorn text-gray-400 mr-1" />
                Rep Campaign
              </label>
              <select
                v-model="repCampaignStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="with_rep_campaigns">
                  With Campaigns
                </option>
                <option value="without_rep_campaigns">
                  Without Campaigns
                </option>
              </select>
            </div>

            <!-- Fulfillment Order Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-truck text-gray-400 mr-1" />
                Fulfillment Orders
              </label>
              <select
                v-model="fulfillmentOrderStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="with_fulfillment_orders">
                  With Orders
                </option>
                <option value="without_fulfillment_orders">
                  Without Orders
                </option>
              </select>
            </div>

            <!-- Comparison Page Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-chart-bar text-gray-400 mr-1" />
                Comparison Pages
              </label>
              <select
                v-model="comparisonPageStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="with_comparison_pages">
                  With Pages
                </option>
                <option value="without_comparison_pages">
                  Without Pages
                </option>
              </select>
            </div>

            <!-- Spotlight Tracking Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-broadcast-tower text-gray-400 mr-1" />
                Spotlight
              </label>
              <select
                v-model="spotlightStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="with_spotlight_pages">
                  With Pages
                </option>
                <option value="without_spotlight_pages">
                  Without Pages
                </option>
                <option value="with_spotlight_errors">
                  ⚠️ With Errors
                </option>
              </select>
            </div>

            <!-- GBP Checklist Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-clipboard-check text-gray-400 mr-1" />
                GBP Checklist
              </label>
              <select
                v-model="gbpChecklistFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="with_gbp_checklist">
                  With Checklist
                </option>
                <option value="without_gbp_checklist">
                  Without Checklist
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Technical Filters -->
        <div
          v-show="activeFilterCategory === 'technical'"
          class="p-4 bg-white border-b border-gray-200">
          <div class="mb-3">
            <p class="text-xs text-gray-500">
              Filter by provisioning, installation, and hosting status
            </p>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <!-- Provisioning Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-server text-gray-400 mr-1" />
                Provisioning Status
              </label>
              <select
                v-model="provisionStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Statuses
                </option>
                <option value="provisioned">
                  ✓ Provisioned
                </option>
                <option value="in_progress">
                  ⏳ In Progress
                </option>
                <option value="failed">
                  ❌ Failed
                </option>
                <option value="na">
                  N/A
                </option>
              </select>
            </div>

            <!-- Installation Responsibility Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-tools text-gray-400 mr-1" />
                Installation By
              </label>
              <select
                v-model="installationResponsibilityFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="our_team">
                  Our Team
                </option>
                <option value="client">
                  Client
                </option>
                <option value="client_with_contact">
                  Client (with contact)
                </option>
                <option value="client_no_contact">
                  Client (no contact)
                </option>
              </select>
            </div>

            <!-- Hosting Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <i class="fas fa-server text-gray-400 mr-1" />
                Hosting Status
              </label>
              <select
                v-model="hostingStatusFilter"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2
                     focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                     duration-200 bg-white">
                <option value="">
                  All Organizations
                </option>
                <option value="we_host">
                  We Host
                </option>
                <option value="not_we_host">
                  We Don't Host
                </option>
                <option value="website_builder_customer">
                  Website Builder Customer
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Summary -->
    <div
      v-if="$refs.table && $refs.table.totalRecords !== undefined"
      class="p-4 border-t border-gray-200">
      <div class="flex items-center justify-between">
        <div class="text-sm text-gray-600">
          <i class="fas fa-building text-gray-400 mr-1" />
          Showing <span class="font-medium text-gray-900">{{ $refs.table.totalRecords }}</span>
          {{ $refs.table.totalRecords === 1 ? 'organization' : 'organizations' }}
          <span
            v-if="hasActiveFilters"
            class="text-indigo-600 font-medium">(filtered)</span>
        </div>
        <div
          v-if="hasActiveFilters"
          class="text-sm">
          <button
            class="text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
            @click="clearFilters">
            <i class="fas fa-times-circle mr-1" />
            Clear all filters
          </button>
        </div>
      </div>
    </div>
    <div class="bg-white shadow-sm rounded-lg border border-gray-200 overflow-x-auto relative">
      <!-- Scroll hint for mobile users -->
      <div
        v-if="isSmallScreen"
        class="bg-blue-50 border-b border-blue-200 px-4 py-2 text-xs text-blue-700 flex items-center md:hidden">
        <i class="fas fa-arrows-alt-h mr-2" />
        <span>Swipe horizontally to see more columns</span>
      </div>
      <VueSlimTables
        ref="table"
        :source="tableSource"
        :columns="columns"
        class="table tech-stack-table"
        :per-page="perPage">
        <template #cell:customer="{ row }">
          <div class="customer-cell min-w-[250px] max-w-[350px]">
            <div class="space-y-1.5">
              <a
                :href="`/organizations/${row.id}`"
                class="customer-name block hover:underline truncate"
                :title="row.name || 'Unnamed Organization'">
                {{ row.name || 'Unnamed Organization' }}
                <span
                  v-if="row.geogridConfigsCount > 0"
                  class="inline-flex items-center ml-2 px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="row.geogridKeywordsWithMetricsCount > 0 ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-600'"
                  :title="`${row.geogridConfigsCount} geogrid${row.geogridConfigsCount === 1 ? '' : 's'} configured${
                    row.geogridKeywordsWithMetricsCount > 0
                      ? ' with ' + row.geogridKeywordsWithMetricsCount + ' keywords processed'
                      : ' (no metrics yet)'
                  }`">
                  <i class="fas fa-map-marked-alt mr-1 text-[10px]" />
                  {{ row.geogridConfigsCount }}
                  <span
                    v-if="row.geogridKeywordsWithMetricsCount > 0"
                    class="ml-1">
                    ({{ row.geogridKeywordsWithMetricsCount }})
                  </span>
                </span>
              </a>
              <div class="customer-id text-xs text-gray-500 truncate">
                {{ row.uniqueId }}
              </div>
              <div class="customer-email text-xs text-gray-600 truncate">
                <i class="fas fa-envelope text-gray-400 mr-1.5 text-xs" />
                <span :title="row.owner || 'No owner assigned'">{{ row.owner || 'No owner assigned' }}</span>
              </div>
              <div class="flex items-center gap-2 mt-2">
                <span :class="`badge ${getPlanBadgeVariant(row.planName).badge}`">
                  <i class="fas fa-cube mr-1 text-xs" />
                  <a
                    v-if="row.planId"
                    :href="`/plans/${row.planId}`"
                    :class="getPlanBadgeVariant(row.planName).text"
                    class="hover:opacity-80 hover:underline transition-all duration-200">
                    {{ row.planName }}
                  </a>
                  <span
                    v-else
                    :class="getPlanBadgeVariant(row.planName).text">
                    Not subscribed
                  </span>
                  <i
                    v-if="row.planId && row.planVisible === false"
                    class="fas fa-eye-slash ml-1 text-xs opacity-70"
                    title="Hidden plan (not visible to customers)" />
                </span>
              </div>
            </div>
            <div class="mt-3 pt-3 border-t border-gray-100">
              <div class="text-xs font-medium text-gray-500 mb-2">
                <i class="fab fa-trello mr-1" />
                Trello Integration
              </div>
              <div
                v-if="row.trelloCustomerCardCreatedAt"
                class="flex flex-wrap items-center gap-1.5">
                <span class="text-xs text-gray-600">Connected</span>
                <a
                  v-if="row.trelloCustomerCardId"
                  :href="`https://trello.com/c/${row.trelloCustomerCardId}`"
                  target="_blank"
                  class="inline-flex items-center justify-center w-7 h-7 rounded bg-blue-50 text-blue-600
                         hover:bg-blue-100 transition-colors"
                  title="Customer Card">
                  <i class="fas fa-user text-xs" />
                </a>
                <a
                  v-if="row.trelloActivationCardId"
                  :href="`https://trello.com/c/${row.trelloActivationCardId}`"
                  target="_blank"
                  class="inline-flex items-center justify-center w-7 h-7 rounded bg-amber-50 text-amber-600
                         hover:bg-amber-100 transition-colors"
                  title="Activation Card">
                  <i class="fas fa-bolt text-xs" />
                </a>
                <a
                  v-if="row.trelloLaunchCardId"
                  :href="`https://trello.com/c/${row.trelloLaunchCardId}`"
                  target="_blank"
                  class="inline-flex items-center justify-center w-7 h-7 rounded bg-purple-50 text-purple-600
                         hover:bg-purple-100 transition-colors"
                  title="Launch Card">
                  <i class="fas fa-rocket text-xs" />
                </a>
                <a
                  v-if="row.trelloExecutionCardId"
                  :href="`https://trello.com/c/${row.trelloExecutionCardId}`"
                  target="_blank"
                  class="inline-flex items-center justify-center w-7 h-7 rounded bg-green-50 text-green-600
                         hover:bg-green-100 transition-colors"
                  title="Execution Card">
                  <i class="fas fa-play text-xs" />
                </a>
                <a
                  v-if="row.trelloPerformanceCardId"
                  :href="`https://trello.com/c/${row.trelloPerformanceCardId}`"
                  target="_blank"
                  class="inline-flex items-center justify-center w-7 h-7 rounded bg-indigo-50 text-indigo-600
                         hover:bg-indigo-100 transition-colors"
                  title="Performance Card">
                  <i class="fas fa-chart-simple text-xs" />
                </a>
              </div>
              <div v-else>
                <div
                  v-if="loadingTrelloId === row.id"
                  class="flex items-center text-xs text-blue-600">
                  <i class="fas fa-spinner fa-spin mr-1.5" />
                  <span>Initiating...</span>
                </div>
                <button
                  v-else
                  class="text-xs text-blue-600 hover:text-blue-700 hover:underline focus:outline-none"
                  @click.prevent="initiateTrello(row.id)">
                  <i class="fas fa-plus-circle mr-1" />
                  Connect Trello
                </button>
              </div>
            </div>

            <div class="mt-2 text-xs text-gray-500">
              <i class="fas fa-clock mr-1" />
              Customer for {{ calculateCustomerDuration(row.createdAt) }}
            </div>
          </div>
        </template>

        <template #cell:customerOwner="{ row }">
          <div class="min-w-[160px]">
            <div class="flex items-center justify-between group">
              <span
                :class="getCustomerOwnerBadgeClass(row.customerOwner)"
                class="status-badge"
                data-controller="tooltip"
                :data-tooltip-content-value="getCustomerOwnerTooltip(row.customerOwner)">
                {{ formatCustomerOwner(row.customerOwner) }}
              </span>
              <button
                class="dropdown-trigger opacity-0 group-hover:opacity-100 transition-opacity"
                @click="toggleCustomerOwnerDropdown(row)">
                <i class="fas fa-pencil-alt text-xs" />
              </button>
            </div>
            <div
              v-if="row.showCustomerOwnerDropdown"
              class="relative mt-2">
              <select
                v-model="row.customerOwner"
                class="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white
                       focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors"
                @change="updateCustomerOwner(row)">
                <option value="linda">
                  Linda
                </option>
                <option value="pin_parrot">
                  Pin Parrot
                </option>
                <option value="constant_social">
                  Constant Social
                </option>
              </select>
            </div>
          </div>
        </template>

        <template #cell:statuses="{ row }">
          <div class="min-w-[180px] max-w-[220px] space-y-2">
            <!-- Onboarding Status -->
            <div class="flex items-center justify-between group">
              <div class="flex items-center gap-2">
                <i class="fas fa-user-check text-gray-400 text-xs" />
                <span
                  :class="getOnboardingBadgeClass(row.onboardingState)"
                  class="status-badge hoverable cursor-pointer"
                  data-controller="tooltip"
                  :data-tooltip-content-value="getOnboardingTooltip(row.onboardingState)"
                  @click="viewOnboardingNotes(row.id, row.name || 'N/A')">
                  {{ formatOnboardingState(row.onboardingState) }}
                </span>
              </div>
            </div>

            <!-- Provisioning Status -->
            <div class="flex items-center gap-2">
              <i class="fas fa-server text-gray-400 text-xs" />
              <span
                :class="getProvisioningBadgeClass(row)"
                class="status-badge"
                data-controller="tooltip"
                :data-tooltip-content-value="getProvisioningTooltip(row)">
                {{ formatProvisioningStatus(row) }}
              </span>
            </div>

            <!-- Sentiment Status -->
            <div class="flex items-center justify-between group">
              <div class="flex items-center gap-2">
                <i class="fas fa-smile text-gray-400 text-xs" />
                <span
                  :class="getSentimentBadgeClass(row.sentiment)"
                  class="status-badge"
                  data-controller="tooltip"
                  :data-tooltip-content-value="getSentimentTooltip(row.sentiment)">
                  {{ formatSentiment(row.sentiment) }}
                </span>
              </div>
              <button
                class="dropdown-trigger opacity-0 group-hover:opacity-100 transition-opacity"
                title="Edit customer sentiment"
                @click="toggleSentimentDropdown(row)">
                <i class="fas fa-pencil-alt text-xs" />
              </button>
            </div>
            <div
              v-if="row.showSentimentDropdown"
              class="mt-1">
              <select
                v-model="row.sentiment"
                class="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white
                       focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors"
                @change="updateSentiment(row)">
                <option value="pending">
                  Pending
                </option>
                <option value="happy">
                  Happy
                </option>
                <option value="ok">
                  OK
                </option>
                <option value="upset">
                  Upset
                </option>
              </select>
            </div>

            <!-- Order Submitted Status -->
            <div class="flex items-center justify-between group">
              <div class="flex items-center gap-2">
                <i class="fas fa-shopping-cart text-gray-400 text-xs" />
                <span
                  :class="getOrderSubmittedBadgeClass(row.orderSubmitted)"
                  class="status-badge"
                  data-controller="tooltip"
                  :data-tooltip-content-value="getOrderSubmittedTooltip(row.orderSubmitted)">
                  {{ formatOrderSubmitted(row.orderSubmitted) }}
                </span>
              </div>
              <button
                class="dropdown-trigger opacity-0 group-hover:opacity-100 transition-opacity"
                title="Edit order status"
                @click="toggleOrderSubmittedDropdown(row)">
                <i class="fas fa-pencil-alt text-xs" />
              </button>
            </div>
            <div
              v-if="row.showOrderSubmittedDropdown"
              class="mt-1">
              <select
                v-model="row.orderSubmitted"
                class="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white
                       focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors"
                @change="updateOrderSubmitted(row)">
                <option :value="false">
                  No
                </option>
                <option :value="true">
                  Yes
                </option>
              </select>
            </div>
          </div>
        </template>

        <template #cell:stripe="{ row }">
          <div class="stripe-cell min-w-[200px] max-w-[250px]">
            <div
              v-if="row.stripeCustomerId"
              class="space-y-2">
              <!-- MRR and Active Subscriptions -->
              <div
                v-if="row.stripeMonthlyRecurringRevenue > 0"
                class="flex items-center gap-2">
                <span class="stripe-metric-badge bg-green-100 text-green-700">
                  <i class="fas fa-sync-alt text-xs mr-1" />
                  ${{ formatCurrency(row.stripeMonthlyRecurringRevenue) }}/mo
                </span>
                <span
                  v-if="row.stripeActiveSubscriptions > 0"
                  class="text-xs text-gray-600">
                  ({{ row.stripeActiveSubscriptions }} {{ row.stripeActiveSubscriptions === 1 ? 'sub' : 'subs' }})
                </span>
              </div>

              <!-- Total Paid -->
              <div class="flex items-center text-sm text-gray-600">
                <i class="fas fa-dollar-sign text-gray-400 mr-1.5 text-xs" />
                <span class="font-medium">Total: ${{ formatCurrency(row.stripeTotalPaid || 0) }}</span>
              </div>

              <!-- Payment Issues Indicators -->
              <div
                v-if="row.stripeDelinquent"
                class="flex items-center">
                <span class="stripe-metric-badge bg-red-100 text-red-700">
                  <i class="fas fa-exclamation-triangle text-xs mr-1" />
                  Delinquent
                </span>
              </div>

              <!-- Outstanding Balance -->
              <div
                v-if="row.stripeTotalOutstanding && row.stripeTotalOutstanding > 0"
                class="flex items-center">
                <span class="stripe-metric-badge bg-orange-100 text-orange-700">
                  <i class="fas fa-exclamation-circle text-xs mr-1" />
                  ${{ formatCurrency(row.stripeTotalOutstanding) }} Outstanding
                </span>
              </div>

              <!-- Failed Payments -->
              <div
                v-if="row.stripeFailedPaymentsCount && row.stripeFailedPaymentsCount > 0"
                class="flex items-center">
                <span class="stripe-metric-badge bg-yellow-100 text-yellow-700">
                  <i class="fas fa-times-circle text-xs mr-1" />
                  {{ row.stripeFailedPaymentsCount }} Failed Payment{{ row.stripeFailedPaymentsCount > 1 ? 's' : '' }}
                </span>
              </div>

              <!-- Discount Status -->
              <div
                v-if="row.stripeHasActiveDiscount"
                class="flex items-center">
                <span class="stripe-metric-badge bg-amber-100 text-amber-700">
                  <i class="fas fa-tag text-xs mr-1" />
                  Discount Active
                </span>
              </div>

              <!-- Last Payment -->
              <div
                v-if="row.stripeLastPaymentDate"
                class="text-xs text-gray-500">
                <i class="fas fa-clock text-gray-400 mr-1" />
                Last: {{ formatDate(row.stripeLastPaymentDate) }}
              </div>

              <!-- Payment Method -->
              <div
                v-if="row.stripeDefaultPaymentMethod"
                class="text-xs text-gray-600 mt-1">
                <i class="fas fa-credit-card text-gray-400 mr-1" />
                {{ row.stripeDefaultPaymentMethod.brand }} •••• {{ row.stripeDefaultPaymentMethod.last4 }}
              </div>
            </div>
            <div
              v-else
              class="text-sm text-gray-400 italic">
              No Stripe data
            </div>
          </div>
        </template>

        <template #cell:moments="{ row }">
          <div class="moments-cell min-w-[200px]">
            <div class="grid grid-cols-2 gap-x-3 gap-y-2 mb-2">
              <div
                v-for="(count, period) in row.momentCounts"
                :key="period"
                class="moment-stat flex items-center justify-between">
                <span class="moment-period text-xs text-gray-500">
                  {{ formatPeriod(period) }}:
                </span>
                <span class="moment-count text-sm font-semibold text-gray-900">
                  {{ count }}
                </span>
              </div>
            </div>
            <div class="pt-2 border-t border-gray-100">
              <div
                :class="getLastMomentClass(row.timeSinceLastMoment)"
                class="text-xs font-medium">
                <i class="fas fa-clock mr-1" />
                Last: {{ formatTimeSinceLastMoment(row.timeSinceLastMoment) }}
              </div>
            </div>
          </div>
        </template>

        <template #cell:features="{ row }">
          <div class="features-cell min-w-[140px] space-y-1">
            <label class="feature-toggle">
              <input
                type="checkbox"
                :checked="row.featureLocations"
                class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                @change="updateFeature(row.id, 'feature_locations', $event.target.checked)">
              <span class="feature-label">
                <i class="fas fa-map-marker-alt text-gray-400 mr-1 text-xs" />
                Locations
              </span>
            </label>
            <label class="feature-toggle">
              <input
                type="checkbox"
                :checked="row.featureChat"
                class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                @change="updateFeature(row.id, 'feature_chat', $event.target.checked)">
              <span class="feature-label">
                <i class="fas fa-comments text-gray-400 mr-1 text-xs" />
                Chat
              </span>
            </label>
            <label class="feature-toggle">
              <input
                type="checkbox"
                :checked="row.featureLeadManagement"
                class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                @change="updateFeature(row.id, 'feature_lead_management', $event.target.checked)">
              <span class="feature-label">
                <i class="fas fa-users text-gray-400 mr-1 text-xs" />
                Leads
              </span>
            </label>
            <label class="feature-toggle">
              <input
                type="checkbox"
                :checked="row.featureReputationManagement"
                class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                @change="updateFeature(row.id, 'feature_reputation_management', $event.target.checked)">
              <span class="feature-label">
                <i class="fas fa-star text-gray-400 mr-1 text-xs" />
                Reputation
              </span>
            </label>
          </div>
        </template>

        <template #cell:actions="{ row }">
          <div class="actions-cell min-w-[120px] flex flex-col gap-1.5 py-2">
            <a
              :href="`/organizations/${row.id}`"
              class="action-button-stack"
              title="View Details">
              <i class="fas fa-eye mr-2 text-gray-500" />
              <span>View</span>
            </a>
            <button
              class="action-button-stack text-green-600 hover:text-green-700"
              title="Impersonate Organization"
              @click="impersonateOrganization(row)">
              <i class="fas fa-user-secret mr-2" />
              <span>Impersonate</span>
            </button>
            <button
              class="action-button-stack"
              title="View Notes"
              @click="openNotesSlideout(row.id)">
              <i class="fas fa-sticky-note mr-2 text-gray-500" />
              <span>Notes</span>
            </button>
            <!-- ADDED: Monthly Reports button -->
            <button
              class="action-button-stack"
              :class="{
                'bg-yellow-50 border-yellow-200': row.needsReport && !row.reportsSkipped,
                'bg-gray-50': row.reportsSkipped
              }"
              :title="getReportsButtonTitle(row)"
              @click="openReportsSlideout(row)">
              <i
                class="fas fa-file-chart-line mr-2"
                :class="{
                  'text-yellow-600': row.needsReport && !row.reportsSkipped,
                  'text-gray-400': row.reportsSkipped,
                  'text-gray-500': !row.needsReport && !row.reportsSkipped
                }" />
              <span class="flex flex-col items-start">
                <span class="flex items-center">
                  Reports
                  <span
                    v-if="row.monthlyReportsCount > 0"
                    class="ml-1 text-xs font-semibold">
                    ({{ row.monthlyReportsCount }})
                  </span>
                  <span
                    v-if="row.needsReport && !row.reportsSkipped"
                    class="ml-1">
                    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      Due
                    </span>
                  </span>
                </span>
                <span
                  v-if="row.daysSinceLastReport && row.needsReport && !row.reportsSkipped"
                  class="text-xs text-yellow-700 mt-0.5">
                  {{ row.daysSinceLastReport }} days overdue
                </span>
              </span>
            </button>
            <button
              class="action-button-stack"
              title="Send Templated Message"
              @click="openMessagesSlideout(row)">
              <i class="fas fa-envelope mr-2 text-gray-500" />
              <span>Send Message</span>
            </button>
            <button
              class="action-button-stack"
              :class="{ 'opacity-50 cursor-not-allowed': !row.websiteInformation || !row.locationsCount }"
              :disabled="!row.websiteInformation || !row.locationsCount"
              :title="getSpotlightButtonTitle(row)"
              @click="openSpotlightPlan(row.id)">
              <i class="fas fa-lightbulb mr-2 text-gray-500" />
              <span>Spotlight Plan</span>
            </button>
            <button
              class="action-button-stack"
              :class="{ 'bg-gray-100': expandedRows[row.id] }"
              :title="expandedRows[row.id] ? 'Collapse Details' : 'Expand Details'"
              @click="toggleRow(row.id)">
              <i
                :class="expandedRows[row.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                class="mr-2 text-gray-500 transition-transform duration-200" />
              <span>{{ expandedRows[row.id] ? 'Collapse' : 'Expand' }}</span>
            </button>
          </div>
        </template>
      </VueSlimTables>
    </div>
  </div>
</template>

<!-- eslint-disable no-console-->
<script>
import qs from 'qs'
import VueSlimTables from 'vue-slim-tables'
import mdtoast from '@dmuy/toast'
import { createApp } from 'vue'
import axios from '../init/axios'
import 'vue-slim-tables/stylesheets'
import { runSwal } from '../common/delete_with_swal'
import '@dmuy/toast/dist/mdtoast.css'
import useSlidePanel from '@/vue_widgets/composables/use_slide_panel'
import OnboardingNotesComponent from './onboarding_notes.vue'
import OrganizationNotes from './organization_notes.vue'
import MonthlyReportsSlideout from './monthly_reports_slideout.vue' // ADDED: Monthly reports slideout
import TemplatedMessagesSlideout from './admin/templated_messages_slideout.vue'
import OrganizationExpandedRow from './organization_expanded_row.vue'
import SpotlightImplementationPlanSlideout from './admin_dashboard/spotlight_implementation_plan_slideout.vue'

export default {
  name: 'TechStackOrganizationsTable',
  components: { VueSlimTables },
  props: {
    clientId: String,
    planGroups: {
      type: Array,
      default: () => []
    },
    plans: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      search: '',
      showDeleted: true,
      customerOwnerFilter: '',
      onboardingStateFilter: '',
      sentimentFilter: '',
      provisionStatusFilter: '',
      orderSubmittedFilter: '',
      geogridStatusFilter: '',
      discountStatusFilter: '',
      repCampaignStatusFilter: '',
      fulfillmentOrderStatusFilter: '',
      comparisonPageStatusFilter: '',
      featureLocationFilter: false,
      featureChatFilter: false,
      featureLeadsFilter: false,
      featureReputationFilter: false,
      spotlightStatusFilter: '',
      installationResponsibilityFilter: '',
      hostingStatusFilter: '',
      paymentStatusFilter: '',
      gbpChecklistFilter: '',
      locationCountFilter: '',
      subscriptionPlanFilter: '',
      subscriptionPlanIdFilter: '',
      monthlyReportsFilter: '',
      triageTicketsFilter: '',
      filtersExpanded: true,
      activeFilterCategory: 'business',
      loadingTrelloId: null,
      perPage: 25,
      expandedRows: {},
      expandedVueApps: [],
      windowWidth: window.innerWidth
    }
  },
  computed: {
    hasActiveFilters() {
      return !!(this.search ||
                this.customerOwnerFilter ||
                !this.showDeleted ||
                this.onboardingStateFilter ||
                this.sentimentFilter ||
                this.provisionStatusFilter ||
                this.orderSubmittedFilter ||
                this.geogridStatusFilter ||
                this.discountStatusFilter ||
                this.repCampaignStatusFilter ||
                this.fulfillmentOrderStatusFilter ||
                this.comparisonPageStatusFilter ||
                this.featureLocationFilter ||
                this.featureChatFilter ||
                this.featureLeadsFilter ||
                this.featureReputationFilter ||
                this.spotlightStatusFilter ||
                this.installationResponsibilityFilter ||
                this.hostingStatusFilter ||
                this.paymentStatusFilter ||
                this.gbpChecklistFilter ||
                this.locationCountFilter ||
                this.subscriptionPlanFilter ||
                this.monthlyReportsFilter ||
                this.triageTicketsFilter)
    },
    isSmallScreen() {
      return this.windowWidth < 768
    }
  },
  watch: {
    search(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(this.$refs.table.refetch, 500)
    },
    showDeleted(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        // Clear expanded rows when filtering
        this.expandedRows = {}
      }, 500)
    },
    customerOwnerFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        // Clear expanded rows when filtering
        this.expandedRows = {}
      }, 500)
    },
    onboardingStateFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    sentimentFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    provisionStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    orderSubmittedFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    geogridStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    discountStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    repCampaignStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    fulfillmentOrderStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    comparisonPageStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    spotlightStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    installationResponsibilityFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    hostingStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    paymentStatusFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    gbpChecklistFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    locationCountFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    subscriptionPlanFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    subscriptionPlanIdFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    featureLocationFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    featureChatFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    featureLeadsFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    featureReputationFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    monthlyReportsFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    },
    triageTicketsFilter(_val) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$refs.table.refetch()
        this.expandedRows = {}
      }, 500)
    }
  },
  created() {
    this.columns = [
      { title: 'Customer', key: 'customer' },
      { title: 'Customer Owner', key: 'customerOwner' },
      { title: 'Statuses', key: 'statuses' },
      { title: 'Stripe', key: 'stripe' },
      { title: 'Moments', key: 'moments' },
      { title: 'Features', key: 'features' },
      { title: 'Actions', key: 'actions' }
    ]
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    // Clean up Vue instances when component is destroyed
    if (this.expandedVueApps) {
      this.expandedVueApps.forEach((app) => {
        if (app.cleanup) app.cleanup()
        app.unmount()
      })
    }
  },
  methods: {
    toggleFilterExpansion() {
      this.filtersExpanded = !this.filtersExpanded
    },

    getBusinessFiltersCount() {
      let count = 0
      if (this.customerOwnerFilter) count += 1
      if (this.onboardingStateFilter) count += 1
      if (this.sentimentFilter) count += 1
      if (this.orderSubmittedFilter) count += 1
      if (this.locationCountFilter) count += 1
      if (this.monthlyReportsFilter) count += 1
      if (this.triageTicketsFilter) count += 1
      return count
    },

    getBillingFiltersCount() {
      let count = 0
      if (this.subscriptionPlanFilter) count += 1
      if (this.subscriptionPlanIdFilter) count += 1
      if (this.paymentStatusFilter) count += 1
      if (this.discountStatusFilter) count += 1
      return count
    },

    getFeaturesFiltersCount() {
      let count = 0
      if (this.featureLocationFilter) count += 1
      if (this.featureChatFilter) count += 1
      if (this.featureLeadsFilter) count += 1
      if (this.featureReputationFilter) count += 1
      if (this.geogridStatusFilter) count += 1
      if (this.repCampaignStatusFilter) count += 1
      if (this.fulfillmentOrderStatusFilter) count += 1
      if (this.comparisonPageStatusFilter) count += 1
      if (this.spotlightStatusFilter) count += 1
      if (this.gbpChecklistFilter) count += 1
      return count
    },

    getTechnicalFiltersCount() {
      let count = 0
      if (this.provisionStatusFilter) count += 1
      if (this.installationResponsibilityFilter) count += 1
      if (this.hostingStatusFilter) count += 1
      return count
    },

    handleResize() {
      this.windowWidth = window.innerWidth
    },

    clearFilters() {
      this.search = ''
      this.showDeleted = true
      this.customerOwnerFilter = ''
      this.onboardingStateFilter = ''
      this.sentimentFilter = ''
      this.provisionStatusFilter = ''
      this.orderSubmittedFilter = ''
      this.geogridStatusFilter = ''
      this.discountStatusFilter = ''
      this.repCampaignStatusFilter = ''
      this.fulfillmentOrderStatusFilter = ''
      this.comparisonPageStatusFilter = ''
      this.featureLocationFilter = false
      this.featureChatFilter = false
      this.featureLeadsFilter = false
      this.featureReputationFilter = false
      this.spotlightStatusFilter = ''
      this.installationResponsibilityFilter = ''
      this.hostingStatusFilter = ''
      this.paymentStatusFilter = ''
      this.gbpChecklistFilter = ''
      this.locationCountFilter = ''
      this.subscriptionPlanFilter = ''
      this.monthlyReportsFilter = ''
      this.triageTicketsFilter = ''
      this.$refs.table.refetch()
    },

    onStateUpdated() {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(this.$refs.table.refetch, 500)
    },

    reload() {
      this.$refs.table.fetchData(this.$refs.table.page)
    },

    onDelete(id) {
      const params = {
        content: 'This will hide the organization. You will be able to restore it later.'
      }

      runSwal(() => {
        axios.delete(`/organizations/${id}`)
        window.location.reload()
      }, params)
    },

    viewOnboardingNotes(id, name) {
      const slidePanel = useSlidePanel()
      slidePanel.open({
        component: OnboardingNotesComponent,
        props: { id, name },
        events: {
          stateUpdated: this.onStateUpdated
        }
      })
    },

    openNotesSlideout(organizationId) {
      const slidePanel = useSlidePanel()
      slidePanel.open({
        component: OrganizationNotes,
        props: { organizationId },
        events: {
          noteAdded: () => {
            mdtoast.success('Note added successfully', { duration: 3000 })
          }
        }
      })
    },
    // ADDED: Open monthly reports slideout
    openReportsSlideout(row) {
      const slidePanel = useSlidePanel()
      const self = this // Store reference to component instance

      slidePanel.open({
        component: MonthlyReportsSlideout,
        props: {
          organizationId: row.id,
          organizationName: row.name
        },
        events: {
          reportCreated: () => {
            // Update the table to refresh report counts
            if (self.$refs.table && self.$refs.table.fetchData) {
              self.$refs.table.fetchData(self.$refs.table.page)
            }
          },
          reportUpdated: () => {
            // No need to refresh table for updates
          },
          reportDeleted: () => {
            // Update the table to refresh report counts
            if (self.$refs.table && self.$refs.table.fetchData) {
              self.$refs.table.fetchData(self.$refs.table.page)
            }
          }
        }
      })
    },

    getReportsButtonTitle(row) {
      if (row.reportsSkipped) {
        return 'Reports have been skipped for this organization'
      } if (row.needsReport) {
        return `${row.nextReportType} report is due (${row.daysSinceLastReport || 0} days overdue)`
      } if (row.monthlyReportsCount > 0) {
        return `View ${row.monthlyReportsCount} reports`
      }
      return 'No reports yet'
    },

    openMessagesSlideout(row) {
      const slidePanel = useSlidePanel()
      slidePanel.open({
        component: TemplatedMessagesSlideout,
        props: {
          organizationId: row.id,
          organizationName: row.name,
          customerOwner: row.customerOwner,
          primaryContact: row.primaryContact,
          secondaryContact: {
            name: row.secondaryContactName,
            email: row.secondaryContactEmail,
            phone: row.secondaryContactPhone
          }
        },
        events: {
          messageSent: () => {
            mdtoast.success('Message sent successfully', { duration: 3000 })
          }
        }
      })
    },

    getSpotlightButtonTitle(row) {
      if (!row.websiteInformation) {
        return 'Website information required to generate Spotlight Implementation Plan'
      }
      if (!row.locationsCount || row.locationsCount === 0) {
        return 'At least one location required to generate Spotlight Implementation Plan'
      }
      return 'Generate Spotlight Implementation Plan'
    },
    openSpotlightPlan(organizationId) {
      const slidePanel = useSlidePanel()
      slidePanel.open({
        component: SpotlightImplementationPlanSlideout,
        props: { organizationId },
        events: {
          planGenerated: () => {
            mdtoast.success('Implementation plan generated', { duration: 3000 })
          }
        }
      })
    },

    async impersonateOrganization(org) {
      if (!confirm(`Are you sure you want to impersonate ${org.name}? You will be logged in as their account.`)) {
        return
      }

      try {
        const response = await fetch(`/ajax/reseller/organizations/${org.id}/impersonate`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          credentials: 'same-origin'
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to impersonate organization')
        }

        const data = await response.json()

        // Redirect to the appropriate dashboard
        if (data.success && data.redirect_url) {
          window.location.href = data.redirect_url
        } else {
          throw new Error('Impersonation failed')
        }
      } catch (err) {
        console.error('Error impersonating organization:', err)
        alert(`Failed to impersonate organization: ${err.message}`)
      }
    },

    updateFeature(organizationId, feature, value) {
      axios.patch(`/ajax/organizations/${organizationId}/update_feature`, {
        feature: feature,
        value: value
      })
        .then((response) => {
          if (response.data.success) {
            mdtoast.success('Updated feature', { duration: 10000 })
            const index = this.$refs.table.items.findIndex((item) => item.id === organizationId)
            if (index !== -1) {
              this.$set(this.$refs.table.items[index], feature, value)
            }
          } else {
            console.error('Failed to update feature:', response.data.message)
          }
        })
        .catch((error) => {
          console.error('Error updating feature:', error)
        })
    },

    onRestore(id) {
      const params = {
        content: 'You will restore the organization and all its users will become active.'
      }

      runSwal(() => {
        axios.put(`/organizations/${id}/restore`)
        window.location.reload()
      }, params)
    },

    getProvisioningBadgeClass(row) {
      if (this.isProvisionStatusNA(row)) {
        return 'bg-gray-100 text-gray-700'
      }
      switch (row.provisionStatus) {
        case 'failed':
          return 'bg-red-100 text-red-700'
        case 'in_progress':
          return 'bg-yellow-100 text-yellow-700'
        case 'provisioned':
          return 'bg-green-100 text-green-700'
        default:
          return 'bg-gray-100 text-gray-700'
      }
    },

    initiateTrello(id) {
      this.loadingTrelloId = id
      axios.post(`/organizations/${id}/initiate_trello`)
        .then((response) => {
          if (response.status === 200) {
            mdtoast.success('Trello Initiated', { duration: 5000 })
            this.$refs.table.refetch()
          } else {
            mdtoast.error('Failed to initiate Trello', { duration: 5000 })
          }
        })
        .catch((error) => {
          console.error('Error initiating Trello:', error)
          mdtoast.error('Error initiating Trello', { duration: 5000 })
        })
        .finally(() => {
          this.loadingTrelloId = null
        })
    },

    formatProvisioningStatus(row) {
      if (this.isProvisionStatusNA(row)) {
        return 'N/A'
      }
      switch (row.provisionStatus) {
        case 'failed':
          return 'Failed'
        case 'in_progress':
          return 'In Progress'
        case 'provisioned':
          return 'OK'
        default:
          return 'Unknown'
      }
    },

    isProvisionStatusNA(row) {
      const status = row.provisionStatus
      return status === 'no_billing_plan' || status === 'pending_welcome_form' || status === 'nothing_to_do'
    },

    tableSource(params) {
      return axios.get(
        '/ajax/organizations/tech_stack_customers',
        {
          params: {
            ...params,
            query: this.search,
            per_page: this.perPage,
            show_deleted: this.showDeleted,
            customer_owner: this.customerOwnerFilter,
            onboarding_state: this.onboardingStateFilter,
            sentiment: this.sentimentFilter,
            provision_status: this.provisionStatusFilter,
            order_submitted: this.orderSubmittedFilter,
            geogrid_status: this.geogridStatusFilter,
            discount_status: this.discountStatusFilter,
            rep_campaign_status: this.repCampaignStatusFilter,
            fulfillment_order_status: this.fulfillmentOrderStatusFilter,
            comparison_page_status: this.comparisonPageStatusFilter,
            feature_locations: this.featureLocationFilter,
            feature_chat: this.featureChatFilter,
            feature_leads: this.featureLeadsFilter,
            feature_reputation: this.featureReputationFilter,
            spotlight_status: this.spotlightStatusFilter,
            installation_responsibility: this.installationResponsibilityFilter,
            hosting_status: this.hostingStatusFilter,
            payment_status: this.paymentStatusFilter,
            gbp_checklist: this.gbpChecklistFilter,
            location_count: this.locationCountFilter,
            subscription_plan: this.subscriptionPlanFilter,
            subscription_plan_id: this.subscriptionPlanIdFilter,
            monthly_reports_status: this.monthlyReportsFilter,
            triage_tickets_status: this.triageTicketsFilter
          },
          paramsSerializer(json) {
            return qs.stringify(json, { arrayFormat: 'brackets' })
          }
        }
      ).then((res) => {
        // Debug: log first organization with stripe data

        if (res.data && res.data.data && res.data.data.length > 0) {
          const firstWithStripe = res.data.data.find((org) => org.stripeCustomerId)
          if (firstWithStripe) {
            console.log('First org with Stripe data:', {
              id: firstWithStripe.id,
              name: firstWithStripe.name,
              stripeCustomerId: firstWithStripe.stripeCustomerId,
              stripeTotalPaid: firstWithStripe.stripeTotalPaid,
              stripeMonthlyRecurringRevenue: firstWithStripe.stripeMonthlyRecurringRevenue
            })
          }

          // Debug moment counts
          const firstWithMoments = res.data.data.find((org) => org.momentCounts &&
            Object.values(org.momentCounts).some((count) => count > 0))
          if (firstWithMoments) {
            console.log('Moment counts example:', {
              name: firstWithMoments.name,
              momentCounts: firstWithMoments.momentCounts,
              rawData: JSON.stringify(firstWithMoments.momentCounts)
            })
          }

          // Check if it's camelCase issue
          const checkCamelCase = res.data.data.find((org) => org.moment_counts)
          if (checkCamelCase) {
            console.log('Found snake_case moment_counts:', checkCamelCase.moment_counts)
          }
        }
        return res.data
      })
    },

    timeSinceCreated(createdAt) {
      return new Date(createdAt).toLocaleDateString()
    },

    getPlanBadgeVariant(planName) {
      if (!planName) return { badge: 'badge-secondary', text: 'text-gray-800' }

      const str = planName.toLowerCase()
      if (str.includes('gpp')) return { badge: 'badge-info', text: 'text-blue-800' }
      if (str.includes('sitesense')) return { badge: 'badge-success', text: 'text-green-800' }
      if (str.includes('bundle')) return { badge: 'badge-purple', text: 'text-purple-800' }
      if (str.includes('jobsite')) return { badge: 'badge-warning', text: 'text-orange-800' }

      return { badge: 'badge-secondary', text: 'text-gray-800' }
    },

    calculateCustomerDuration(createdAt) {
      const now = new Date()
      const created = new Date(createdAt)
      const diffTime = Math.abs(now - created)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      const years = Math.floor(diffDays / 365)
      const months = Math.floor((diffDays % 365) / 30)
      const days = diffDays % 30

      let duration = ''
      if (years > 0) {
        duration += `${years} year${years > 1 ? 's' : ''} `
      }
      if (months > 0) {
        duration += `${months} month${months > 1 ? 's' : ''} `
      }
      if (days > 0 || duration === '') {
        duration += `${days} day${days !== 1 ? 's' : ''}`
      }

      return duration.trim()
    },
    getOnboardingBadgeClass(state) {
      switch (state) {
        case 'pending_contact':
          return 'bg-red-100 text-red-700'
        case 'contacted':
          return 'bg-blue-100 text-blue-700'
        case 'onboarded':
          return 'bg-green-100 text-green-700'
        default:
          return 'bg-gray-100 text-gray-700'
      }
    },

    formatPeriod(period) {
      const periods = {
        // Handle both snake_case and camelCase
        last_30_days: '30d',
        last30Days: '30d',
        last_90_days: '90d',
        last90Days: '90d',
        last_180_days: '180d',
        last180Days: '180d',
        last_12_months: '12m',
        last12Months: '12m'
      }
      return periods[period] || period
    },

    formatTimeSinceLastMoment(seconds) {
      if (seconds === null) return 'Never'
      const days = Math.floor(seconds / (24 * 60 * 60))
      if (days < 30) return `${days}d ago`
      const months = Math.floor(days / 30)
      return `${months}m ago`
    },

    getLastMomentClass(seconds) {
      if (seconds === null) return 'text-red-600 font-bold animate-pulse'
      const days = Math.floor(seconds / (24 * 60 * 60))
      if (days < 7) return 'text-green-600'
      if (days < 30) return 'text-yellow-600'
      return 'text-red-600'
    },

    formatOnboardingState(state) {
      return state.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    },

    getSentimentBadgeClass(sentiment) {
      switch (sentiment) {
        case 'happy':
          return 'bg-green-100 text-green-700'
        case 'ok':
          return 'bg-yellow-100 text-yellow-700'
        case 'upset':
          return 'bg-red-100 text-red-700'
        default:
          return 'bg-gray-100 text-gray-700'
      }
    },

    formatSentiment(sentiment) {
      return sentiment.charAt(0).toUpperCase() + sentiment.slice(1)
    },

    toggleSentimentDropdown(row) {
      row.showSentimentDropdown = !row.showSentimentDropdown
    },

    updateSentiment(row) {
      axios.patch(`/ajax/organizations/${row.id}/update_sentiment`, {
        sentiment: row.sentiment
      })
        .then((response) => {
          if (response.data.success) {
            mdtoast.success('Updated sentiment', { duration: 5000 })
            row.showSentimentDropdown = false
          } else {
            console.error('Failed to update sentiment:', response.data.message)
            mdtoast.error('Failed to update sentiment', { duration: 5000 })
          }
        })
        .catch((error) => {
          console.error('Error updating sentiment:', error)
          mdtoast.error('Error updating sentiment', { duration: 5000 })
        })
    },

    getOrderSubmittedBadgeClass(orderSubmitted) {
      return orderSubmitted ?
        'bg-green-100 text-green-700' :
        'bg-gray-100 text-gray-700'
    },

    formatOrderSubmitted(orderSubmitted) {
      return orderSubmitted ? 'Yes' : 'No'
    },

    toggleOrderSubmittedDropdown(row) {
      row.showOrderSubmittedDropdown = !row.showOrderSubmittedDropdown
    },

    updateOrderSubmitted(row) {
      axios.patch(`/ajax/organizations/${row.id}/update_order_submitted`, {
        order_submitted: row.orderSubmitted
      })
        .then((response) => {
          if (response.data.success) {
            mdtoast.success('Updated order submitted status', { duration: 5000 })
            row.showOrderSubmittedDropdown = false
          } else {
            console.error('Failed to update order submitted status:', response.data.message)
            mdtoast.error('Failed to update order submitted status', { duration: 5000 })
          }
        })
        .catch((error) => {
          console.error('Error updating order submitted status:', error)
          mdtoast.error('Error updating order submitted status', { duration: 5000 })
        })
    },

    getCustomerOwnerBadgeClass(customerOwner) {
      switch (customerOwner) {
        case 'linda':
          return 'bg-blue-100 text-blue-700'
        case 'pin_parrot':
          return 'bg-green-100 text-green-700'
        case 'constant_social':
          return 'bg-purple-100 text-purple-700'
        default:
          return 'bg-gray-100 text-gray-700'
      }
    },

    formatCustomerOwner(customerOwner) {
      const owners = {
        linda: 'Linda',
        pin_parrot: 'Pin Parrot',
        constant_social: 'Constant Social'
      }
      return owners[customerOwner] || 'Linda'
    },

    toggleCustomerOwnerDropdown(row) {
      row.showCustomerOwnerDropdown = !row.showCustomerOwnerDropdown
    },

    updateCustomerOwner(row) {
      axios.patch(`/ajax/organizations/${row.id}/update_customer_owner`, {
        customer_owner: row.customerOwner
      })
        .then((response) => {
          if (response.data.success) {
            mdtoast.success('Updated customer owner', { duration: 5000 })
            row.showCustomerOwnerDropdown = false
          } else {
            console.error('Failed to update customer owner:', response.data.message)
            mdtoast.error('Failed to update customer owner', { duration: 5000 })
          }
        })
        .catch((error) => {
          console.error('Error updating customer owner:', error)
          mdtoast.error('Error updating customer owner', { duration: 5000 })
        })
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0'
      // Convert string to number if needed
      const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
      return numAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    formatDate(dateString) {
      if (!dateString) return 'Never'
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays}d ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
      if (diffDays < 365) return `${Math.floor(diffDays / 30)}m ago`
      return `${Math.floor(diffDays / 365)}y ago`
    },

    debounce(func, wait) {
      let timeout
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout)
          func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    },

    getOnboardingTooltip(state) {
      switch (state) {
        case 'pending_contact':
          return 'Customer has not been contacted yet. Click to view/add notes.'
        case 'contacted':
          return 'Initial contact made with customer. Click to view/add notes.'
        case 'onboarded':
          return 'Customer onboarding completed. Click to view notes.'
        default:
          return 'Click to view onboarding notes'
      }
    },

    getProvisioningTooltip(row) {
      if (this.isProvisionStatusNA(row)) {
        return 'Provisioning not applicable for this customer'
      }
      switch (row.provisionStatus) {
        case 'failed':
          return 'Account provisioning failed - requires attention'
        case 'in_progress':
          return 'Account provisioning in progress'
        case 'provisioned':
          return 'All services successfully provisioned'
        default:
          return 'Provisioning status unknown'
      }
    },

    getSentimentTooltip(sentiment) {
      switch (sentiment) {
        case 'happy':
          return 'Customer is satisfied with our services'
        case 'ok':
          return 'Customer has neutral sentiment'
        case 'upset':
          return 'Customer needs attention - may have concerns'
        case 'pending':
          return 'Customer sentiment not yet assessed'
        default:
          return 'Customer sentiment status'
      }
    },

    getOrderSubmittedTooltip(orderSubmitted) {
      return orderSubmitted ?
        'Customer has submitted their order' :
        'Customer has not yet submitted an order'
    },

    getCustomerOwnerTooltip(owner) {
      switch (owner) {
        case 'linda':
          return 'Customer managed by Linda'
        case 'pin_parrot':
          return 'Customer managed by Pin Parrot'
        case 'constant_social':
          return 'Customer managed by Constant Social'
        default:
          return 'Customer owner not assigned'
      }
    },

    toggleRow(rowId) {
      this.expandedRows[rowId] = !this.expandedRows[rowId]
      // Force update after a short delay to ensure table has rendered
      setTimeout(() => {
        this.injectExpandedRows()
      }, 100)
    },

    injectExpandedRows() {
      const tbody = this.$refs.table?.$el?.querySelector('tbody')
      if (!tbody) return

      // Clean up existing Vue instances with animation
      if (this.expandedVueApps) {
        this.expandedVueApps.forEach((app) => {
          // Find the wrapper and animate it closed
          const row = tbody.querySelector('tr.expanded-row')
          if (row) {
            const wrapper = row.querySelector('.expanded-content-wrapper')
            if (wrapper) {
              wrapper.style.maxHeight = '0'
              wrapper.style.opacity = '0'
              // Wait for animation then unmount
              setTimeout(() => {
                if (app.cleanup) app.cleanup()
                if (app.cleanup) app.cleanup()
                app.unmount()
                row.remove()
              }, 300)
            } else {
              if (app.cleanup) app.cleanup()
              app.unmount()
              row.remove()
            }
          } else {
            if (app.cleanup) app.cleanup()
            app.unmount()
          }
        })
      }
      this.expandedVueApps = []

      // Small delay to allow cleanup animations
      setTimeout(() => {
        // Remove any remaining expanded rows
        tbody.querySelectorAll('.expanded-row').forEach((row) => row.remove())

        // Get all regular rows
        const rows = tbody.querySelectorAll('tr')

        // Iterate through rows and add expanded content where needed
        rows.forEach((row) => {
        // Skip if this is already an expanded row
          if (row.classList.contains('expanded-row')) return

          // Find the organization link in this row
          const link = row.querySelector('a[href*="/organizations/"]')
          if (!link) return

          // Extract organization ID from the link
          const match = link.href.match(/\/organizations\/([^/]+)/)
          if (!match) return

          const orgId = match[1]

          // If this row should be expanded, add the expanded content
          if (this.expandedRows[orgId]) {
            const expandedRow = document.createElement('tr')
            expandedRow.className = 'expanded-row'
            const expandedTd = document.createElement('td')
            expandedTd.setAttribute('colspan', '7')
            expandedTd.className = 'p-0 bg-stone-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-hidden'
            const wrapperHtml = '<div class="expanded-content-wrapper" ' +
              'style="max-height: 0; opacity: 0; overflow: hidden; transition: max-height 0.5s ease-out, opacity 0.3s ease-out;">' +
              '<div class="p-6"></div></div>'
            expandedTd.innerHTML = wrapperHtml
            expandedRow.appendChild(expandedTd)
            row.insertAdjacentElement('afterend', expandedRow)

            // Mount Vue components
            this.$nextTick(() => {
              const contentDiv = expandedTd.querySelector('.p-6')
              const wrapperDiv = expandedTd.querySelector('.expanded-content-wrapper')

              const expandedApp = createApp(OrganizationExpandedRow, {
                organizationId: orgId
              })
              expandedApp.mount(contentDiv)

              // Trigger the animation after mounting
              setTimeout(() => {
                // Calculate actual content height and set it
                const contentHeight = contentDiv.scrollHeight
                wrapperDiv.style.maxHeight = `${contentHeight + 100}px` // Add some buffer
                wrapperDiv.style.opacity = '1'

                // After animation completes, remove height constraints
                setTimeout(() => {
                  wrapperDiv.style.maxHeight = 'none'
                  wrapperDiv.style.overflow = 'visible'
                }, 500) // Match the transition duration
              }, 10)

              // Store the app with the org ID for easier tracking
              expandedApp.orgId = orgId
              this.expandedVueApps.push(expandedApp)

              // Listen for content changes to recalculate height
              const recalculateHeight = () => {
                if (wrapperDiv && contentDiv) {
                  const newHeight = contentDiv.scrollHeight
                  wrapperDiv.style.maxHeight = `${newHeight + 100}px`
                }
              }

              window.addEventListener('intercom-content-changed', recalculateHeight)

              // Store cleanup function
              expandedApp.cleanup = () => {
                window.removeEventListener('intercom-content-changed', recalculateHeight)
              }
            })
          }
        })
      }, 50) // Close setTimeout
    }

  }
}
</script>
<!-- eslint-enable no-console-->

<style scoped>
/* Ensure table is scrollable on mobile */
:deep(.tech-stack-table) {
  min-width: 1200px;
}

:deep(.tech-stack-table td) {
  @apply align-top;
  white-space: normal;
}

/* Improve table responsiveness */
@media (max-width: 1024px) {
  :deep(.tech-stack-table) {
    font-size: 0.875rem;
  }

  .action-button-stack {
    @apply px-2 py-1.5 text-xs;
    min-width: 80px;
  }

  .action-button-stack i {
    @apply mr-1;
  }
}

@media (max-width: 768px) {
  :deep(.tech-stack-table) {
    font-size: 0.75rem;
  }

  .customer-cell {
    min-width: 200px !important;
  }

  .moments-cell {
    min-width: 150px !important;
  }
}

/* Text overflow handling */
.truncate {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes expandRow {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.expanded-row td {
  animation: expandRow 0.3s ease-out;
}

.expanded-row {
  transition: all 0.3s ease-out;
}

.expanded-row-content {
  animation: expandRow 0.4s ease-out;
}

.stripe-metric-badge {
  @apply inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap;
}

.stripe-cell {
  @apply space-y-1.5;
}

.action-button-stack {
  @apply flex items-center justify-start px-3 py-2 text-sm font-medium text-gray-700
         bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-900
         transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500
         focus:ring-offset-1 shadow-sm hover:shadow whitespace-nowrap;
  min-width: 100px;
}

.action-button-stack:active {
  @apply bg-gray-100 shadow-inner;
}

.moments-cell {
  @apply space-y-2;
}

.moment-stat {
  @apply flex items-center justify-between;
}

.moment-period {
  @apply text-xs font-medium text-gray-500;
}

.moment-count {
  @apply text-sm font-semibold text-gray-900;
}

/* Customer cell styles */
.customer-cell {
  @apply py-2;
}

.customer-name {
  @apply text-sm font-medium text-gray-900;
}

.customer-id {
  @apply font-mono;
}

.customer-email {
  @apply flex items-center;
}

/* Plan badge styles */
.plan-badge {
  @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap;
}

/* Status badge styles */
.status-badge {
  @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap;
}

/* Feature toggle styles */
.feature-toggle {
  @apply flex items-center cursor-pointer;
}

.feature-toggle input {
  @apply h-3.5 w-3.5;
}

.feature-label {
  @apply ml-2 text-xs text-gray-700 whitespace-nowrap;
}

/* Actions cell responsive */
.actions-cell {
  @apply min-w-[100px];
}

/* Dropdown trigger */
.dropdown-trigger {
  @apply p-1 rounded hover:bg-gray-100 transition-colors;
}
</style>
